import React, { useState } from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { TranslationViewModel } from '../../models/settings';
import { SettingsService } from '../../hooks/SettingsService';
import { Loader } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { TranslationEditByField, TranslationEditRowData } from '../../components';

interface i18nElement {
  key?: 'string';
  value?: 'string';
}
interface TranslationUpdateModalProps {
  opened: boolean;
  onModalAction: (result: boolean) => void;
  translation: TranslationViewModel;
}

const TranslationUpdateModal: React.FC<any> = ({ opened, onModalAction, translation }: TranslationUpdateModalProps) => {
  const classes = useStyles();
  const settingsService = new SettingsService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [isBusyUpdating, setIsBusyUpdating] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [language, setLanguage] = React.useState('');

  const [translationsObject, setTranslations] = React.useState('');
  const [translationsArray, setTranslationArray] = React.useState([] as i18nElement[]);

  const [filterValue, setFilterValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filteredTranslations, setFilteredTranslations] = React.useState([] as i18nElement[]);

  const [tab, setTab] = useState(0);

  const loadTranslation = async () => {
    setIsBusy(true);
    setLoaded(false);

    settingsService
      .getTranslationByIsoCode(translation.isoCode)
      .then((result) => {
        setLanguage(translation.language);
        setTranslations(JSON.stringify(result));
        convertTranslationsToArray(result);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        closeDialog(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (opened) {
      const load = async () => loadTranslation();
      load();
    }
  }, [opened]);

  const closeDialog = (result: boolean) => {
    onModalAction(result);
    clearFields();
  };

  // reset states
  const clearFields = () => {
    setTab(0);
    setLanguage('');
    setFilterValue('');
    setTranslations('');
    setTranslationArray([]);
    setFilteredTranslations([]);
  };

  const handleUpdateTranslation = () => {
    if (!validated()) {
      return;
    }

    setIsBusyUpdating(true);

    settingsService
      .updateTranslation(translation.id, language, translationsObject)
      .then(() => {
        notifications.success(t('f-trans-upd-ok'));
        closeDialog(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusyUpdating(false);
      });
  };

  const validated = (): boolean => {
    if (!language) {
      notifications.warning(t('f-val-lang-trans'));
      return false;
    }

    return true;
  };

  // convert object to array
  const convertTranslationsToArray = (data: string) => {
    const array: i18nElement[] = Object.entries(data).map(([key, value]) => ({ key, value }) as i18nElement);
    setTranslationArray(array);
  };

  const handleChangeField = (key: string, newValue: string) => {
    // create a copy of translations object
    const translationsCopy: any = Object.assign({}, JSON.parse(translationsObject));

    if (key) {
      // update i18n key with new value
      translationsCopy[key] = newValue;
    }

    // update i18n object
    setTranslations(JSON.stringify(translationsCopy));
    // convert back to array to reflect changes on UI
    convertTranslationsToArray(translationsCopy);

    if (filterValue && isSearching) {
      const position = filteredTranslations.findIndex((item) => item.key === key);
      const newElement = { key: key, value: newValue } as i18nElement;
      filteredTranslations.splice(position, 1, newElement);
    }
  };

  const handleTabChange = (_event: React.ChangeEvent<any>, value: number) => {
    setTab(value);
  };

  const handleUpdateTranslations = (newObject: string) => {
    setTranslations(newObject);
    // reflect new changes within translations array
    convertTranslationsToArray(JSON.parse(newObject));
  };

  const handleApplySearch = () => {
    setIsSearching(true);
    const filtered: i18nElement[] = translationsArray.filter((item) =>
      item.value!.toLowerCase().includes(filterValue.toLowerCase())
    );
    if (!filtered?.length) {
      setFilteredTranslations(translationsArray);
    } else {
      setFilteredTranslations(filtered);
    }
  };

  const handleChangeSearchValue = (searchValue: string) => {
    setFilterValue(searchValue);
  };

  const handleResetSearch = () => {
    setFilterValue('');
    setIsSearching(false);
    setFilteredTranslations([]);
  };

  const arrayTranslations = filteredTranslations.length ? filteredTranslations : translationsArray;

  return (
    <Dialog
      id="updTransDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      className={classes.dialog}
      maxWidth="lg"
    >
      <DialogTitle id="updTransDialogTitle" className={classes.dialogHeaderFooter}>
        {t('f-trans-upd-title')}
      </DialogTitle>

      <DialogContent>
        <Grid container direction="column" spacing={2}>
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <Grid item container md={12} spacing={3}>
              <Grid item md={5} xs={12}>
                <TextField
                  id="comment-input"
                  label={t('f-settings-lang')}
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                />
              </Grid>

              <Grid item md={12} xs={12}>
                <Tabs value={tab} onChange={handleTabChange} centered>
                  <Tab label={t('f-trans-by-field')} />
                  <Tab label={t('f-trans-by-raw')} />
                </Tabs>

                {tab === 0 && (
                  <TranslationEditByField
                    searchValue={filterValue}
                    translations={arrayTranslations}
                    resetSearch={handleResetSearch}
                    applySearch={handleApplySearch}
                    handleChangeField={handleChangeField}
                    setSearchValue={handleChangeSearchValue}
                  />
                )}

                {tab === 1 && (
                  <TranslationEditRowData handleChange={handleUpdateTranslations} translations={translationsObject} />
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusyUpdating && <CircularProgress />}

        <Button
          onClick={() => closeDialog(false)}
          variant="outlined"
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={handleUpdateTranslation}
          id="saveLocationBtn"
          disabled={isBusyUpdating}
          startIcon={<Icon>{'translate'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-com-update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationUpdateModal;
